import { captureException } from '@sentry/capacitor';
import { isFunction } from 'radash';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import MetaMobileAnalytics, {
  ACTION_VIA_SOURCE,
  SignUpEventData,
  useAnalytics,
} from '../../analytics';
import { useUser } from '../../context/User';
import { isHttpError } from '../../services/helpers/authorizedRequests';
import { registerContact } from '../../services/users/UsersService';
import { EmailVerification } from '../EmailVerification';
import { useTikTokPixelTracker } from '../TikTokPixel';

const className = 'c-Register';

interface Props {
  handleCompleted: () => void;
  initiator?: ACTION_VIA_SOURCE.FREE_TRIAL;
  email: string;
  password: string;
}

const CompleteRegistration = ({
  handleCompleted,
  initiator,
  email,
  password,
}: Props) => {
  const [isSigningIn, setIsSigningIn] = useState(false);

  const { signIn } = useUser();

  const analyticsContext = useAnalytics();
  const { trackTikTokPixel } = useTikTokPixelTracker();

  const doRegisterContact = () => {
    registerContact().catch((e) => {
      const errorMessage = isHttpError(e)
        ? e.body.message
        : 'Registering contact failed';

      captureException(new Error(errorMessage), {
        extra: { email },
      });
    });
  };

  const loginUser = async () => {
    const trackingData: SignUpEventData = {
      method: 'Email',
    };

    if (initiator === ACTION_VIA_SOURCE.FREE_TRIAL) {
      analyticsContext.trackSignUpViaFreeTrial(trackingData);
    } else {
      analyticsContext.trackSignUp(trackingData);
    }

    MetaMobileAnalytics.sendSignUpEvent();
    await trackTikTokPixel({
      event_id: uuidv4(),
      event_name: 'CompleteRegistration',
      email: email.toLowerCase(),
    });

    setIsSigningIn(true);
    await signIn(email, password);
    doRegisterContact();
    setIsSigningIn(false);

    if (isFunction(handleCompleted)) {
      handleCompleted();
    }
  };

  return (
    <div className={className}>
      <EmailVerification
        isSigningIn={isSigningIn}
        email={email}
        onSuccess={loginUser}
      />
    </div>
  );
};

export default CompleteRegistration;
